import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Layers, Type } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  isLayoutBrollClip,
  isTitleOverlayClip,
  normalizeHostOverlayOpacity,
} from '@/lib/videoEditorHostOverlays';
import { TitleStyleControls } from '@/components/reelsShorts/ReelsShortsTitleTemplates';
import { sanitizeTitleStyle } from '@/lib/reelsShortsTitle';

export function ReelsLayoutInspector({ clip, onChange }) {
  const [expanded, setExpanded] = useState(true);
  const clipId = clip?.id || '';

  useEffect(() => {
    setExpanded(true);
  }, [clipId]);

  if (!clip) return null;
  const isTitle = isTitleOverlayClip(clip);
  const isLayout = isLayoutBrollClip(clip);
  const isCard = Boolean(clip.carouselGroupId);
  const heading = isTitle ? 'Title text' : isLayout ? 'B-ROLL pane' : isCard ? 'Card text' : 'Overlay text';
  const summary = isTitle
    ? [clip.titleLine1, clip.titleLine2].filter(Boolean).join(' · ')
    : isCard
      ? (clip.cardTitle || clip.displayText || '')
      : (clip.displayText || clip.generationPrompt || clip.triggerText || '');

  const set = (patch) => onChange?.(patch);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="reels-layout-inspector"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => setExpanded((open) => !open)}
        data-testid="reels-layout-inspector-toggle"
      >
        {isLayout ? <Layers className="h-3.5 w-3.5 shrink-0 text-cyan-300" /> : <Type className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-300">
          {heading}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">
          {summary || 'Click to expand'}
        </span>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
      </button>

      {expanded ? (
        <div className="flex flex-col gap-2">
          {isTitle ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="min-w-0">
                <span className="mb-0.5 block text-[10px] text-slate-500">Line 1</span>
                <Input
                  value={clip.titleLine1 || ''}
                  onChange={(event) => set({ titleLine1: event.target.value.slice(0, 80) })}
                  className="h-8 border-[#2a3344] bg-[#0d1117] text-xs text-white"
                  data-testid="reels-layout-title-line1"
                />
              </label>
              <label className="min-w-0">
                <span className="mb-0.5 block text-[10px] text-slate-500">Line 2</span>
                <Input
                  value={clip.titleLine2 || ''}
                  onChange={(event) => set({ titleLine2: event.target.value.slice(0, 80) })}
                  className="h-8 border-[#2a3344] bg-[#0d1117] text-xs text-white"
                  data-testid="reels-layout-title-line2"
                />
              </label>
            </div>
          ) : isCard ? (
            <label className="min-w-0">
              <span className="mb-0.5 block text-[10px] text-slate-500">Card label</span>
              <Input
                value={clip.cardTitle || clip.displayText || ''}
                onChange={(event) => {
                  const value = event.target.value.slice(0, 160);
                  set({ cardTitle: value, displayText: value });
                }}
                className="h-8 border-[#2a3344] bg-[#0d1117] text-xs text-white"
                data-testid="reels-layout-card-label"
              />
            </label>
          ) : (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              <label className="min-w-0">
                <span className="mb-0.5 block text-[10px] text-slate-500">On-screen text</span>
                <Input
                  value={clip.displayText || clip.triggerText || ''}
                  onChange={(event) => set({ displayText: event.target.value.slice(0, 400) })}
                  className="h-8 border-[#2a3344] bg-[#0d1117] text-xs text-white"
                  data-testid="reels-layout-display-text"
                />
              </label>
              <label className="min-w-0 lg:col-span-2">
                <span className="mb-0.5 block text-[10px] text-slate-500">Image / background prompt</span>
                <Textarea
                  value={clip.generationPrompt || ''}
                  onChange={(event) => set({ generationPrompt: event.target.value.slice(0, 800) })}
                  rows={3}
                  className="min-h-[72px] border-[#2a3344] bg-[#0d1117] text-xs text-white"
                  data-testid="reels-layout-generation-prompt"
                />
              </label>
            </div>
          )}
          {isTitle ? (
            <TitleStyleControls
              style={clip.titleStyle}
              onChange={(nextStyle) => set({ titleStyle: sanitizeTitleStyle(nextStyle) })}
            />
          ) : null}
          {!isTitle ? (
            <label className="flex flex-col gap-1.5" data-testid="reels-layout-opacity">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] text-slate-500">Opacity</span>
                <span className="text-[11px] font-semibold tabular-nums text-slate-300">
                  {Math.round(normalizeHostOverlayOpacity(clip.opacity) * 100)}%
                </span>
              </div>
              <Slider
                min={5}
                max={100}
                step={1}
                value={[Math.round(normalizeHostOverlayOpacity(clip.opacity) * 100)]}
                onValueChange={([pct]) => {
                  set({ opacity: normalizeHostOverlayOpacity((Number(pct) || 100) / 100) });
                }}
                data-testid="reels-layout-opacity-slider"
              />
            </label>
          ) : null}
          {isLayout ? (
            <p className="text-[10px] text-slate-500">
              On the preview, click the cyan B-ROLL pane, then drag the gold handles to expand it.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
